import React from "react";

import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Link2,
  type LucideIcon,
  Quote,
  X,
} from "lucide-react";

import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorCitation as Citation } from "../../../Types/XFactorBriefing";
import type {
  XFactorBand,
  XFactorMove,
  XFactorPoint,
  XFactorScoreTile,
  XFactorSectionBubble,
} from "../../../Types/XFactorViewsRendering";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";
import { toText } from "../../XFactorBriefings/components/Primitives";
import { BAND_FILL, bandOf, horizonLabel, SECTION_LABEL } from "../lib/derive";

/**
 * Design tokens read off the live xfactor page via computed styles, not eyeballed.
 *
 * The band colours are Tailwind's 500 ramp. xfactor renders every score the
 * same way — a tint of the band at 12% behind near-black text — and applies the
 * SAME banding to risk as to everything else. Risk 38 renders red there and
 * risk 79 amber, so no inversion anywhere. See `bandOf` in lib/derive.
 */
export const ACCENT = "#ad46ff";

export const BAND_TEXT: Record<XFactorBand, string> = {
  strong: "#169244",
  moderate: "#ae8404",
  weak: "#b13030",
  unknown: "#90a1b9",
};

/** Surface recipe shared by every list item: border-border/60 + bg-muted/20. */
export const CARD_SURFACE =
  "rounded-md border border-[#e3e8f0]/60 bg-[#f0f5fa]/20";

/** Host of a citation URL, for the "· sec.gov" suffix. */
export const hostOf = (url?: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
};

/** Muted body colour used for the "— description" half of a row. */
export const MUTED = "#62748e";

/* ------------------------------------------------------------------ */
/* Modal                                                                */
/* ------------------------------------------------------------------ */

/**
 * xfactor's modals are wide, light-chrome overlays with the title top-left and
 * a bare close affordance top-right. Escape closes; the backdrop closes.
 */
export const Modal: React.FC<{
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Header controls, right-aligned before the close button. */
  actions?: React.ReactNode;
  /** Fixed bar under the title (e.g. section tabs) — does not scroll with body. */
  subheader?: React.ReactNode;
  /** Pinned bar under the scroll area — navigation, not content. */
  footer?: React.ReactNode;
  width?: string;
  children: React.ReactNode;
}> = ({
  open,
  onClose,
  title,
  subtitle,
  actions,
  subheader,
  footer,
  width = "max-w-6xl",
  children,
}) => {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:p-8"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`w-full ${width} rounded-xl bg-white shadow-2xl`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-gray-900">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
            ) : null}
          </div>
          {actions}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>
        {subheader ? (
          <div className="shrink-0 border-b border-gray-200 bg-gray-100">
            {subheader}
          </div>
        ) : null}
        {/* 80vh plus half an inch. The overlay behind this scrolls, so going a
            little past the viewport is safe — it buys a row of content rather
            than clipping it. */}
        <div className="max-h-[calc(80vh+3rem)] overflow-y-auto px-6 py-5">
          {children}
        </div>
        {footer ? (
          <div className="border-t border-gray-100 bg-gray-50/60 px-6 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Score presentation                                                   */
/* ------------------------------------------------------------------ */

/**
 * A score tile.
 *
 * xfactor's exact recipe, measured from computed styles: fill is the band
 * colour at 12%, border is 1px of the same at 45%, radius 10px, padding
 * 8px/12px. Label is 10px semibold uppercase with 0.5px tracking; the value is
 * 24px semibold in a darkened band colour.
 */
export const ScoreStrip: React.FC<{ tiles: XFactorScoreTile[] }> = ({
  tiles,
}) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}
    >
      {tiles.map(tile => {
        const band = tile.band ?? bandOf(tile.value, bhThresholds);
        const base = BAND_FILL[band];
        const word = tile.value == null && tile.display ? tile.display : null;
        const reasoning = toText(tile.rationale);
        return (
          <div
            key={tile.label}
            title={reasoning || undefined}
            style={{
              backgroundColor: `${base}1f`,
              border: `1px solid ${base}73`,
              borderRadius: 10,
              padding: "8px 12px",
            }}
          >
            <div
              className="uppercase"
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.5px",
                color: BAND_TEXT[band],
              }}
            >
              {tile.label}
            </div>
            <div
              style={{
                // A word (KPI momentum) sits a size down from a number, as in
                // xfactor's MomentumTile, and keeps the same line box.
                fontSize: word ? 18 : 24,
                fontWeight: 600,
                lineHeight: word ? "28.8px" : 1.2,
                color: BAND_TEXT[band],
              }}
            >
              {word ?? (tile.value == null ? "—" : Math.round(tile.value))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** A compact score chip — used in card headers and modal hero rows. */
export const ScoreChip: React.FC<{
  label: string;
  value: number | null;
}> = ({ label, value }) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const band = bandOf(value, bhThresholds);
  const base = BAND_FILL[band];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs"
      style={{
        backgroundColor: `${base}1f`,
        border: `1px solid ${base}73`,
        color: BAND_TEXT[band],
      }}
    >
      <span className="opacity-80">{label}</span>
      <span className="font-semibold">
        {value == null ? "—" : Math.round(value)}
      </span>
    </span>
  );
};

/** The active/inactive tab pills (Public Data · KPI Metrics, card faces). */
export const TabPill: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-lg px-2 py-1 transition-colors"
    style={{
      fontSize: 11,
      fontWeight: active ? 600 : 500,
      backgroundColor: active ? `${ACCENT}26` : "transparent",
      color: active ? ACCENT : MUTED,
    }}
  >
    {children}
  </button>
);

/**
 * Bubble geometry, matched to EB's existing Public Data chart.
 *
 * Relevance is **quantised**, not continuous: that chart's own legend reads
 * `L (25%) · M (50%) · H (100%)`, three steps across a 4x range. The previous
 * continuous scale here spanned only 19.6px to 30px — the smallest dot was 65%
 * of the largest, which made the size channel effectively invisible.
 *
 * The fill tops out well inside the track so the grey ring stays visible even
 * at H, as it does on the existing chart. That chart's `ScoreBubble` uses a
 * 40px track with 32 / 16 / 8px fills — 0.8 / 0.4 / 0.2 of the track — so the
 * ratios below reproduce it at this table's tighter row height. A fill of 28 on
 * a 30px track left a ~1px ring that read as no ring at all.
 */
const TRACK = 30;
const TRACK_FILL_MAX = 24;

const RELEVANCE_FILL: Record<XFactorBand, number> = {
  strong: 1,
  moderate: 0.5,
  weak: 0.25,
  unknown: 0.25,
};

/** Tooltip shorthand: health reads R/Y/G, relevance reads L/M/H. */
const HEALTH_LETTER: Record<XFactorBand, string> = {
  strong: "G",
  moderate: "Y",
  weak: "R",
  unknown: "—",
};

const RELEVANCE_LETTER: Record<XFactorBand, string> = {
  strong: "H",
  moderate: "M",
  weak: "L",
  unknown: "—",
};

/** A matrix bubble. Colour is health, fill diameter is relevance. */
export const Bubble: React.FC<{
  bubble: XFactorSectionBubble;
  onClick?: () => void;
  /** Column index — generating rows blink left → right. */
  waveIndex?: number;
  /** What the diameter encodes, for the tooltip. KPI bubbles size by weight. */
  sizeLabel?: string;
  /** Tooltip text for that value, when `relevance` is a scaled stand-in. */
  sizeValueText?: string;
}> = ({
  bubble,
  onClick,
  waveIndex = 0,
  sizeLabel = "Relevance",
  sizeValueText,
}) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const healthBand = bandOf(bubble.score, bhThresholds);
  const generating = Boolean(bubble.generating);
  const empty = Boolean(bubble.missing) && !generating;
  const fraction = RELEVANCE_FILL[bandOf(bubble.relevance)];
  const fill = generating
    ? Math.round(TRACK_FILL_MAX * 0.85)
    : Math.round(TRACK_FILL_MAX * fraction);
  // Empty (dashed) dots have nothing useful to show — no native hover tip.
  const title = empty
    ? undefined
    : generating
      ? `${bubble.label} — Briefing generating`
      : [
          bubble.label,
          `Health: ${HEALTH_LETTER[bubble.band]} (${
            bubble.score == null ? "—" : Math.round(bubble.score)
          })`,
          `${sizeLabel}: ${RELEVANCE_LETTER[bandOf(bubble.relevance)]} (${
            sizeValueText ??
            (bubble.relevance == null ? "—" : Math.round(bubble.relevance))
          })`,
        ].join(" — ");
  const ariaLabel = empty ? `${bubble.label} — no score` : title;

  return (
    <button
      type="button"
      onClick={empty ? undefined : onClick}
      title={title}
      aria-label={ariaLabel}
      aria-busy={generating || undefined}
      aria-disabled={empty || undefined}
      tabIndex={empty ? -1 : undefined}
      className={`flex h-10 w-full items-center justify-center${
        empty
          ? " cursor-default"
          : generating
            ? ""
            : " transition-transform hover:scale-110"
      }`}
    >
      {/* Opaque white disc sits above the row rail so the hairline runs
          behind the bubble instead of through it. The grey ring is layered
          on top to keep the same track colour as the existing chart. */}
      <span
        className="relative z-[1] flex items-center justify-center rounded-full"
        style={{
          width: TRACK,
          height: TRACK,
          backgroundColor: "#fff",
          border:
            bubble.missing && !generating ? "1.5px dashed #cbd5e1" : "none",
        }}
      >
        {bubble.missing && !generating ? null : (
          <>
            <span
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{
                backgroundColor: "rgba(156, 163, 175, 0.3)",
              }}
            />
            <span
              className={`relative block rounded-full${
                generating ? " matrix-bubble-blink" : ""
              }`}
              style={{
                width: fill,
                height: fill,
                backgroundColor: generating ? "#94a3b8" : BAND_FILL[healthBand],
                ...(generating
                  ? ({ "--bubble-wave": waveIndex } as React.CSSProperties)
                  : null),
              }}
            />
          </>
        )}
      </span>
    </button>
  );
};

/** The traffic-light dot on the Account Synopsis section rail. */
export const RailDot: React.FC<{
  band: XFactorBand;
  missing: boolean;
  onClick?: () => void;
  title?: string;
}> = ({ band, missing, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className="flex h-8 w-8 items-center justify-center rounded-full transition-transform hover:scale-110"
  >
    <span
      className="block h-4 w-4 rounded-full"
      style={{
        backgroundColor: missing ? "transparent" : BAND_FILL[band],
        border: missing ? "1.5px dashed #cbd5e1" : "none",
      }}
    />
  </button>
);

/* ------------------------------------------------------------------ */
/* Content blocks                                                       */
/* ------------------------------------------------------------------ */

export const ColumnHeading: React.FC<{
  children: React.ReactNode;
  count?: number;
  tone?: string;
}> = ({ children, count, tone = "text-gray-500" }) => (
  <div className="mb-2 flex items-center gap-2">
    <h3 className={`text-[11px] font-medium uppercase tracking-wide ${tone}`}>
      {children}
    </h3>
    {count != null ? (
      <span className="rounded bg-gray-100 px-1.5 text-[10px] font-medium text-gray-500">
        {count}
      </span>
    ) : null}
  </div>
);

/**
 * A Must Know / Watch-Out / Highlight entry.
 *
 * Measured recipe: `rounded-md border border-border/60 bg-muted/20 px-2.5 py-2`,
 * title 12px/600, body 12px/400, tag 10px/400 uppercase with 0.25px tracking.
 * Evidence is a 12px Link2 button top-right that opens the Citations modal —
 * xfactor does not print citation chips inline.
 */
export const PointCard: React.FC<{
  point: XFactorPoint;
  citations?: Citation[];
  /** Tighter vertical padding, as the Executive Brief rows use. */
  dense?: boolean;
  /**
   * Suppress the confidence/severity chip. The Account Synopsis columns carry
   * no such chip in xfactor even though the payload has one on every item;
   * the section detail modal does show it.
   */
  hideChip?: boolean;
  /** 1-based position, when the list is numbered as in the section modal. */
  index?: number;
  /**
   * How the entry sits in its container. xfactor only draws a border around an
   * entry when it stands alone; inside a tinted group card the entries are bare
   * rows ("bare") or white rows separated by a divider ("row").
   */
  surface?: "card" | "bare" | "row";
}> = ({ point, citations, dense, hideChip, index, surface = "card" }) => {
  const [showCitations, setShowCitations] = React.useState(false);
  const hasRefs = point.refs.length > 0;
  // Inside a group card xfactor floats the category as a pill on the right of
  // the title row instead of a lowercase caption under the body.
  const tagOnRight = surface !== "card" && Boolean(point.tag);
  const wrapper =
    surface === "card"
      ? `${CARD_SURFACE} px-2.5 ${dense ? "py-1.5" : "py-2"}`
      : surface === "row"
        ? "bg-white px-3 py-2.5"
        : "px-1 py-1.5";

  return (
    <>
      <div className={`${wrapper} flex items-start justify-between gap-2`}>
        <div className="flex min-w-0 gap-2">
          {index != null ? (
            <span className="mt-px shrink-0 text-[11px] font-semibold text-gray-400">
              {index}
            </span>
          ) : null}
          <div className="min-w-0">
            {point.title ? (
              <div
                style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
                className="text-gray-900"
              >
                {point.title}
                {point.body && dense ? (
                  <span style={{ fontWeight: 400, color: MUTED }}>
                    {" "}
                    — {point.body}
                  </span>
                ) : null}
              </div>
            ) : null}
            {point.body && !dense ? (
              <p
                style={{ fontSize: 12, lineHeight: 1.45, color: MUTED }}
                className="mt-0.5"
              >
                {point.body}
              </p>
            ) : null}

            {/* xfactor prints these as labelled lines under the body rather
                than folding them into it. */}
            {point.implication ? (
              <p style={{ fontSize: 12, lineHeight: 1.45 }} className="mt-0.5">
                <span className="font-semibold text-gray-700">
                  {point.implicationLabel || "Implication"}:{" "}
                </span>
                <span style={{ color: MUTED }}>{point.implication}</span>
              </p>
            ) : null}
            {point.outcome ? (
              <p style={{ fontSize: 12, lineHeight: 1.45 }} className="mt-0.5">
                <span className="font-semibold text-gray-700">
                  Expected outcome:{" "}
                </span>
                <span style={{ color: MUTED }}>{point.outcome}</span>
              </p>
            ) : null}
            {point.timeframe ? (
              <p style={{ fontSize: 12, lineHeight: 1.45 }} className="mt-0.5">
                <span className="font-semibold text-gray-700">Timeframe: </span>
                <span style={{ color: MUTED }}>
                  {horizonLabel(point.timeframe)}
                </span>
              </p>
            ) : null}

            <div className="mt-1 flex flex-wrap items-center gap-2">
              {point.tag && !tagOnRight ? (
                <span
                  className="uppercase"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.25px",
                    color: MUTED,
                  }}
                >
                  {point.tag}
                </span>
              ) : null}
              {point.chip && !hideChip ? (
                <span
                  className="rounded bg-gray-100 px-1.5 uppercase text-gray-600"
                  style={{ fontSize: 10, letterSpacing: "0.25px" }}
                >
                  {point.chip}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {tagOnRight ? (
            <span
              className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-gray-600"
              style={{ fontSize: 10, lineHeight: 1.6 }}
            >
              {point.tag}
            </span>
          ) : null}
          {hasRefs ? (
            <button
              type="button"
              onClick={() => setShowCitations(true)}
              title={`${point.refs.length} cited source${
                point.refs.length === 1 ? "" : "s"
              }`}
              aria-label="Show citations"
              className="shrink-0 rounded p-0.5 transition-colors"
              style={{ color: ACCENT }}
            >
              <Link2 className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      </div>

      <CitationsModal
        open={showCitations}
        onClose={() => setShowCitations(false)}
        title={point.title}
        refs={point.refs}
        citations={citations}
      />
    </>
  );
};

export const PointList: React.FC<{
  points: XFactorPoint[];
  citations?: Citation[];
  dense?: boolean;
  hideChip?: boolean;
  numbered?: boolean;
  empty?: string;
  /** See `PointCard.surface`. */
  variant?: "card" | "bare" | "row";
}> = ({
  points,
  citations,
  dense,
  hideChip,
  numbered,
  empty = "Nothing in this payload.",
  variant = "card",
}) =>
  points.length ? (
    <div
      className={
        variant === "row"
          ? "divide-y divide-gray-100 overflow-hidden rounded-md bg-white"
          : variant === "bare"
            ? "divide-y divide-black/5"
            : "space-y-2"
      }
    >
      {points.map((p, i) => (
        <PointCard
          key={i}
          point={p}
          citations={citations}
          dense={dense}
          hideChip={hideChip}
          index={numbered ? i + 1 : undefined}
          surface={variant}
        />
      ))}
    </div>
  ) : (
    <p className="text-xs italic text-gray-400">{empty}</p>
  );

/**
 * A tinted, collapsible group panel — xfactor's frame for Highlights,
 * Lowlights, Opportunities, Risks, Recommended Actions, Key Content and
 * Citations inside a section body.
 *
 * The tint carries the verdict (green for upside, red for downside, purple for
 * the things to do) and the icon repeats the left rail's, so the rail and the
 * body read as the same list.
 */
const GROUP_TONE = {
  green: {
    bg: "#f2fdf5",
    border: "#d3f2de",
    icon: "#169244",
    title: "#0f172a",
  },
  red: { bg: "#fef4f4", border: "#fbdcdc", icon: "#b13030", title: "#0f172a" },
  accent: { bg: "#fdf6ff", border: "#f2ddfb", icon: ACCENT, title: ACCENT },
  plain: {
    bg: "#ffffff",
    border: "#e3e8f0",
    icon: "#62748e",
    title: "#0f172a",
  },
} as const;

export const GroupCard: React.FC<{
  title: string;
  Icon: LucideIcon;
  tone?: keyof typeof GROUP_TONE;
  id?: string;
  children: React.ReactNode;
}> = ({ title, Icon, tone = "plain", id, children }) => {
  const [open, setOpen] = React.useState(true);
  const t = GROUP_TONE[tone];
  const Chevron = open ? ChevronUp : ChevronDown;

  return (
    <section
      id={id}
      className="mt-3 rounded-lg border"
      style={{ backgroundColor: t.bg, borderColor: t.border }}
    >
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <h3
          className="flex items-center gap-2 text-[13px] font-semibold"
          style={{ color: t.title }}
        >
          <Icon size={15} style={{ color: t.icon }} aria-hidden />
          {title}
        </h3>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          title={open ? "Collapse" : "Expand"}
          className="rounded p-1 text-gray-400 transition-colors hover:bg-white/70 hover:text-[#ad46ff]"
        >
          <Chevron size={15} />
        </button>
      </div>
      {open ? <div className="px-3 pb-3">{children}</div> : null}
    </section>
  );
};

/**
 * The Citations modal behind every evidence link.
 *
 * xfactor renders "<claim> is based on:" then one row per cited source —
 * ref id, linked title, and a source/date/domain line. 720px wide,
 * 10px radius, 24px padding.
 */
export const CitationsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string | null;
  refs: string[];
  citations?: Citation[];
}> = ({ open, onClose, title, refs, citations }) => {
  const byId = new Map((citations || []).map(c => [c.id, c]));
  const resolved = refs.map(r => ({ ref: r, c: byId.get(r) }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="max-w-[720px]"
      title="Citations"
    >
      {title ? (
        <p className="mb-3 text-sm text-gray-900">
          <span className="font-semibold">{title}</span>
          <span className="text-gray-500"> is based on:</span>
        </p>
      ) : null}

      <ol className="space-y-2.5">
        {resolved.map(({ ref, c }) => (
          <li key={ref} className="flex gap-2">
            <span className="mt-0.5 shrink-0 font-mono text-[11px] text-gray-400">
              [{ref}]
            </span>
            <div className="min-w-0">
              {c?.url ? (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm underline underline-offset-2"
                  style={{ color: "#1f2937" }}
                >
                  {c.title || c.url}
                  <ExternalLink size={11} aria-hidden />
                </a>
              ) : (
                <span className="text-sm text-gray-500">
                  {c?.title || "Source not present in this section's citations"}
                </span>
              )}
              {c?.source || c?.published_at || c?.url ? (
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
                  {c?.source ? (
                    <span className="rounded bg-gray-100 px-1 py-0.5 font-medium uppercase">
                      {c.source}
                    </span>
                  ) : null}
                  {c?.published_at ? <span>{c.published_at}</span> : null}
                  {/* xfactor closes the line with the host, e.g. "· sec.gov". */}
                  {hostOf(c?.url) ? (
                    <span className="text-gray-400">· {hostOf(c?.url)}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </Modal>
  );
};

/** A recommended play, with its timeframe rail and owner.
 * Typography matches Must Know / Watch-outs PointCards (12px titles, muted tags).
 */
export const MoveCard: React.FC<{ move: XFactorMove; index?: number }> = ({
  move,
  index,
}) => (
  <div className={`${CARD_SURFACE} px-2.5 py-2`}>
    <div className="flex items-center gap-2">
      {move.timeframe ? (
        <span
          className="rounded border border-gray-200 bg-white px-1.5 py-0.5 tracking-wide text-gray-700"
          style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
        >
          {horizonLabel(move.timeframe)}
        </span>
      ) : null}
      {move.priority ? (
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600">
          {move.priority}
        </span>
      ) : null}
      {move.score != null ? (
        <span className="text-[10px] font-medium text-gray-400">
          Score {Math.round(move.score)}
        </span>
      ) : null}
    </div>
    <p
      className="mt-1"
      style={{ fontSize: 12, lineHeight: 1.45, color: MUTED }}
    >
      {index != null ? `${index}. ` : ""}
      {move.action}
    </p>
    {move.rationale ? (
      <p
        className="mt-0.5"
        style={{ fontSize: 12, lineHeight: 1.45, color: MUTED }}
      >
        {move.rationale}
      </p>
    ) : null}
    <dl
      className="mt-1 space-y-0.5"
      style={{ fontSize: 12, lineHeight: 1.45, color: MUTED }}
    >
      {move.owner ? (
        <div>
          <dt className="inline uppercase" style={{ letterSpacing: "0.25px" }}>
            Owner:{" "}
          </dt>
          <dd className="inline">{move.owner}</dd>
        </div>
      ) : null}
      {move.outcome ? (
        <div>
          <dt className="inline uppercase" style={{ letterSpacing: "0.25px" }}>
            Outcome:{" "}
          </dt>
          <dd className="inline">{move.outcome}</dd>
        </div>
      ) : null}
      {move.trigger ? (
        <div>
          <dt className="inline uppercase" style={{ letterSpacing: "0.25px" }}>
            Trigger:{" "}
          </dt>
          <dd className="inline">{move.trigger}</dd>
        </div>
      ) : null}
    </dl>
  </div>
);

export const MoveList: React.FC<{
  moves: XFactorMove[];
  numbered?: boolean;
}> = ({ moves, numbered }) =>
  moves.length ? (
    <div className="space-y-2">
      {moves.map((m, i) => (
        <MoveCard key={i} move={m} index={numbered ? i + 1 : undefined} />
      ))}
    </div>
  ) : (
    <p className="text-xs italic text-gray-400">No plays in this payload.</p>
  );

/** A pull-quote band — xfactor renders the account motion this way. */
export const QuoteBand: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    className="flex items-start gap-2 rounded-lg border-l-4 bg-purple-50/60 px-4 py-3 text-sm font-medium leading-snug text-gray-800"
    style={{ borderColor: ACCENT }}
  >
    <Quote
      size={14}
      className="mt-0.5 shrink-0"
      style={{ color: ACCENT }}
      aria-hidden
    />
    <span>{children}</span>
  </div>
);

export const Section: React.FC<{
  title: string;
  id?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, id, right, children }) => (
  <section id={id} className="mt-6">
    <div className="mb-2 flex items-center justify-between gap-3">
      <h3
        className="text-[11px] font-semibold uppercase tracking-wide"
        style={{ color: ACCENT }}
      >
        {title}
      </h3>
      {right}
    </div>
    {children}
  </section>
);

/**
 * A collapsible panel, as the Executive Brief wraps each of its blocks.
 *
 * Bordered card with a chevron collapse control at the top-right;
 * collapsing keeps the heading and hides the body.
 */
export const CollapsibleSection: React.FC<{
  title: string;
  id?: string;
  /** Nested blocks (Customer's World, Business Health) sit a level in. */
  nested?: boolean;
  children: React.ReactNode;
}> = ({ title, id, nested, children }) => {
  const [open, setOpen] = React.useState(true);
  return (
    <section
      id={id}
      className={`${nested ? "mt-3" : "mt-4"} rounded-lg border border-[#e3e8f0]/70 bg-white`}
    >
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <h3
          className={`font-semibold uppercase tracking-wide ${nested ? "text-[10px]" : "text-[11px]"}`}
          style={{ color: ACCENT }}
        >
          {title}
        </h3>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          title={open ? "Collapse" : "Expand"}
          className="rounded p-1 text-gray-400 transition-colors hover:bg-purple-50 hover:text-[#ad46ff]"
        >
          {open ? (
            <ChevronDown size={14} aria-hidden />
          ) : (
            <ChevronRight size={14} aria-hidden />
          )}
        </button>
      </div>
      {open ? <div className="px-3 pb-3">{children}</div> : null}
    </section>
  );
};

/** Stale / partial / missing markers, matching xfactor's badge language. */
export const StatusBadge: React.FC<{
  kind: "stale" | "partial" | "missing";
}> = ({ kind }) => {
  const map = {
    stale: { label: "STALE", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
    partial: {
      label: "PARTIAL",
      cls: "bg-amber-50 text-amber-700 ring-amber-200",
    },
    missing: {
      label: "NO PAYLOAD",
      cls: "bg-gray-100 text-gray-500 ring-gray-200",
    },
  }[kind];
  return (
    <span
      className={`ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold tracking-wide ring-1 ring-inset ${map.cls}`}
    >
      {map.label}
    </span>
  );
};

/** Partial badge plus the digest sections that did not arrive. */
export const PartialStatus: React.FC<{
  missingSectionIds?: string[] | null;
}> = ({ missingSectionIds }) => {
  const labels = (missingSectionIds || [])
    .map(id => SECTION_LABEL[id] || id)
    .filter(Boolean);
  if (!labels.length) return <StatusBadge kind="partial" />;
  return (
    <span
      className="ml-1.5 inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold tracking-wide ring-1 ring-inset bg-amber-50 text-amber-700 ring-amber-200"
      title={`Not generated: ${labels.join(", ")}`}
    >
      PARTIAL
      <span className="truncate font-medium normal-case tracking-normal text-amber-800/80">
        {labels.join(", ")}
      </span>
    </span>
  );
};

export const Legend: React.FC = () => {
  const bh = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const concernHi = Math.max(bh.concern_min, bh.stable_min - 1);
  const stableHi = Math.max(bh.stable_min, bh.strong_above - 1);
  return (
    <div className="flex flex-wrap items-center gap-4 text-[11px] text-gray-500">
      {(
        [
          ["strong", `${bh.strong_above}–100`],
          ["moderate", `${bh.stable_min}–${stableHi}`],
          ["weak", `${bh.concern_min}–${concernHi}`],
          ["unknown", "not scored"],
        ] as Array<[XFactorBand, string]>
      ).map(([band, label]) => (
        <span key={band} className="inline-flex items-center gap-1.5">
          <span
            className="block h-3 w-3 rounded-full"
            style={{
              backgroundColor:
                band === "unknown" ? "transparent" : BAND_FILL[band],
              border: band === "unknown" ? "1.5px dashed #cbd5e1" : "none",
            }}
          />
          {label}
        </span>
      ))}
      <span className="ml-2 inline-flex items-center gap-3 border-l border-gray-200 pl-4">
        {(
          [
            ["L (25%)", 0.25],
            ["M (50%)", 0.5],
            ["H (100%)", 1],
          ] as Array<[string, number]>
        ).map(([label, fraction]) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <span
              className="flex items-center justify-center rounded-full"
              style={{ width: 14, height: 14, backgroundColor: "#e9edf3" }}
            >
              <span
                className="block rounded-full bg-gray-400"
                style={{ width: 13 * fraction, height: 13 * fraction }}
              />
            </span>
            {label}
          </span>
        ))}
      </span>
      <span className="text-gray-400">
        Colour = health · fill = relevance (Fit)
      </span>
    </div>
  );
};
